import { featureFlags } from "@kaggle/sdk";
import { websocketDisconnectionPublisher } from "../../connector/channels/websocketDisconnectionChannel";
import { detectAndPublishErrorInformation } from "../../connector/detectAndPublishErrorInformation";
import { handleCellError } from "../../connector/handleCellError";
import { getIsJupyterDevToolsEnabled } from "../selectors/session";
import { AppAction, FrameDispatch, FrameEvent } from "../types";

/** All the jupyter action types. */
export const enum FrameActionKey {
  CAPTURE_FRAME_EVENT = "CAPTURE_FRAME_EVENT",
  CLEAR_FRAME_EVENTS = "CLEAR_FRAME_EVENTS",
}

/** These will be passed through Redux inside the frame but not stored. */
export interface CaptureFrameEventAction
  extends AppAction<
    FrameActionKey.CAPTURE_FRAME_EVENT,
    { event: FrameEvent; retain: boolean }
  > {}

/** These will be passed through Redux inside the frame but not stored. */
export interface ClearFrameEventsAction
  extends AppAction<FrameActionKey.CLEAR_FRAME_EVENTS> {}

/** All the jupyter actions. */
export type FrameAction = CaptureFrameEventAction | ClearFrameEventsAction;

/** Internal action dispatched when frame events are captured. */
const captureFrameEventInternal = (
  event: FrameEvent,
  retain: boolean
): CaptureFrameEventAction => ({
  type: FrameActionKey.CAPTURE_FRAME_EVENT,
  payload: { event, retain },
});

/**
 * Notify redux that a jupyter event occurred.
 *
 * @remarks
 * These events are only retained when Jupyter dev tools are enabled.
 */
export const captureFrameEvent = (
  event: FrameEvent,
  dispatch: FrameDispatch,
  getState: any
) => {
  if (event.name === "finished_execute.CodeCell") {
    detectAndPublishErrorInformation(
      event,
      featureFlags.kernelsStackOverflowSearch ? handleCellError : undefined
    );
  }
  if (event.name === "kernel_connection_failed.Kernel") {
    websocketDisconnectionPublisher(window.parent)({});
  }
  const isJupyterDevToolsEnabled = getIsJupyterDevToolsEnabled(getState());
  dispatch(captureFrameEventInternal(event, isJupyterDevToolsEnabled));
};

/** Clear all jupyter events tracked in Redux (for JupyterDevTools purposes). */
export const clearFrameEvents = (): ClearFrameEventsAction => ({
  type: FrameActionKey.CLEAR_FRAME_EVENTS,
  payload: {},
});
